(() => {
  if (customElements.get('lf-predictive-search')) return;
  class LFPredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      this.status = this.querySelector('[data-predictive-status]');
      this.details = this.closest('details');
      this.sequence = 0;
      this.selected = -1;
      this.input.setAttribute('role', 'combobox');
      this.input.setAttribute('aria-autocomplete', 'list');
      this.input.setAttribute('aria-haspopup', 'listbox');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.addEventListener('input', () => this.schedule());
      this.input.addEventListener('search', () => this.schedule());
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim() && this.results.hidden) this.schedule();
      });
      this.input.addEventListener('keydown', event => this.onKeydown(event));
      this.querySelector('[data-search-close]').addEventListener('click', () => {
        this.reset();
        this.details.querySelector('summary').click();
      });
      this.details.addEventListener('toggle', () => {
        if (!this.details.open) this.reset();
      });
      this.querySelector('form').addEventListener('submit', () => {
        this.controller?.abort();
        clearTimeout(this.timer);
      });
    }
    disconnectedCallback() { this.reset(); }
    reset() {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.sequence += 1;
      this.results.replaceChildren();
      this.results.hidden = true;
      this.results.removeAttribute('aria-busy');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.selected = -1;
      this.status.textContent = '';
    }
    schedule() {
      this.reset();
      const query = this.input.value.trim();
      if (!query) return;
      const sequence = this.sequence;
      this.timer = setTimeout(() => this.search(query, sequence), 200);
    }
    async search(query, sequence) {
      this.controller = new AbortController();
      this.results.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.loading;
      try {
        const url = new URL(this.dataset.url, location.origin);
        url.searchParams.set('q', query);
        url.searchParams.set('section_id', 'predictive-search');
        url.searchParams.set('resources[type]', 'product');
        url.searchParams.set('resources[limit]', '5');
        const response = await fetch(url, { signal: this.controller.signal, headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Search unavailable');
        const text = await response.text();
        if (sequence !== this.sequence || !this.details.open) return;
        const content = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-predictive-response]');
        if (!content) throw new Error('Search unavailable');
        const list = content.querySelector('[role="listbox"]');
        const count = Number(content.dataset.count) || 0;
        if (list && count > 0) {
          list.id = this.input.getAttribute('aria-controls');
          content.querySelectorAll('[data-suggestion]').forEach((option, index) => {
            option.id = list.id + '-option-' + index;
          });
          this.results.replaceChildren(...Array.from(content.childNodes));
          this.results.hidden = false;
          this.input.setAttribute('aria-expanded', 'true');
        }
        this.status.textContent = this.dataset.results.replace('__count__', count);
      } catch (error) {
        if (error.name !== 'AbortError' && sequence === this.sequence) this.status.textContent = this.dataset.unavailable;
      } finally {
        if (sequence === this.sequence) this.results.removeAttribute('aria-busy');
      }
    }
    onKeydown(event) {
      const options = [...this.results.querySelectorAll('[data-suggestion]')];
      if (this.results.hidden || !options.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.selected = event.key === 'ArrowDown' ? (this.selected + 1) % options.length : (this.selected < 0 ? options.length - 1 : (this.selected - 1 + options.length) % options.length);
        options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.selected)));
        const option = options[this.selected];
        this.input.setAttribute('aria-activedescendant', option.id);
        option.scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && this.selected >= 0) {
        event.preventDefault();
        options[this.selected].querySelector('a').click();
      } else if (event.key === 'Escape') {
        this.reset();
      }
    }
  }
  customElements.define('lf-predictive-search', LFPredictiveSearch);
})();
